package diana.soleil.design.iraniancanadienbazzar.feature.business

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.wrapContentWidth
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.KeyboardType
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppBottomSheet
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppChip
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppTextField
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import diana.soleil.design.iraniancanadienbazzar.core.model.ListingRequestType
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.Res
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.business_listing_request_done
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.business_listing_request_email_label
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.business_listing_request_failed
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.business_listing_request_message_label
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.business_listing_request_message_placeholder
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.business_listing_request_sent
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.business_listing_request_submit
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.business_listing_request_subtitle
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.business_listing_request_title
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.business_listing_request_type_correction
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.business_listing_request_type_privacy
import diana.soleil.design.iraniancanadienbazzar.feature.business.resources.business_listing_request_type_removal
import org.jetbrains.compose.resources.stringResource

/**
 * The no-login "remove / correct my listing" sheet — the privacy takedown/correction path. Works
 * for anyone (no account required); collects a request type, a free-text message, and an optional
 * reply-to email, then hands them to the VM ([BusinessDetailViewModel.submitListingRequest]).
 */
@OptIn(ExperimentalLayoutApi::class)
@Composable
fun ListingRequestSheet(
    status: ListingRequestUiStatus,
    onSubmit: (type: ListingRequestType, message: String, email: String) -> Unit,
    onDismiss: () -> Unit,
) {
    var type by remember { mutableStateOf(ListingRequestType.Correction) }
    var message by remember { mutableStateOf("") }
    var email by remember { mutableStateOf("") }

    AppBottomSheet(
        onDismissRequest = onDismiss,
        title = stringResource(Res.string.business_listing_request_title),
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = AppTheme.spacing.lg)
                .padding(bottom = AppTheme.spacing.lg),
            verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
        ) {
            if (status == ListingRequestUiStatus.Sent) {
                Text(
                    text = stringResource(Res.string.business_listing_request_sent),
                    style = AppTheme.typography.bodyMd,
                    color = AppTheme.colors.onSurface,
                )
                AppButton(
                    text = stringResource(Res.string.business_listing_request_done),
                    onClick = onDismiss,
                    modifier = Modifier.fillMaxWidth(),
                )
                return@Column
            }

            Text(
                text = stringResource(Res.string.business_listing_request_subtitle),
                style = AppTheme.typography.bodySm,
                color = AppTheme.colors.onSurfaceVariant,
            )

            // Request-type chips.
            FlowRow(
                horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
                verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
                modifier = Modifier.fillMaxWidth(),
            ) {
                TypeChip(ListingRequestType.Removal, type, stringResource(Res.string.business_listing_request_type_removal)) { type = it }
                TypeChip(ListingRequestType.Correction, type, stringResource(Res.string.business_listing_request_type_correction)) { type = it }
                TypeChip(ListingRequestType.Privacy, type, stringResource(Res.string.business_listing_request_type_privacy)) { type = it }
            }

            AppTextField(
                value = message,
                onValueChange = { message = it },
                label = stringResource(Res.string.business_listing_request_message_label),
                placeholder = stringResource(Res.string.business_listing_request_message_placeholder),
                singleLine = false,
                modifier = Modifier.fillMaxWidth(),
            )
            AppTextField(
                value = email,
                onValueChange = { email = it },
                label = stringResource(Res.string.business_listing_request_email_label),
                keyboardType = KeyboardType.Email,
                modifier = Modifier.fillMaxWidth(),
            )
            if (status == ListingRequestUiStatus.Failed) {
                Text(
                    text = stringResource(Res.string.business_listing_request_failed),
                    style = AppTheme.typography.bodySm,
                    color = AppTheme.colors.error,
                )
            }
            AppButton(
                text = stringResource(Res.string.business_listing_request_submit),
                onClick = { onSubmit(type, message.trim(), email.trim()) },
                enabled = message.isNotBlank() && status != ListingRequestUiStatus.Sending,
                loading = status == ListingRequestUiStatus.Sending,
                modifier = Modifier.fillMaxWidth(),
            )
        }
    }
}

@Composable
private fun TypeChip(
    value: ListingRequestType,
    selected: ListingRequestType,
    label: String,
    onSelect: (ListingRequestType) -> Unit,
) {
    AppChip(
        label = label,
        selected = value == selected,
        onClick = { onSelect(value) },
        modifier = Modifier.wrapContentWidth(),
    )
}
